<template>
  <div class="brands__wrapper">
    <!-- Header Section -->
    <div
      v-if="data.kicker || data.title || data.description"
      class="brands__wrapper__header"
    >
      <h2
        v-if="data.kicker"
        class="brands__wrapper__header--kicker"
      >
        {{ data.kicker }}
      </h2>
      <h1
        v-if="data.title"
        class="brands__wrapper__header--title"
      >
        {{ data.title }}
      </h1>
      <p
        v-if="data.description"
        class="brands__wrapper__header--description"
      >
        {{ data.description }}
      </p>
    </div>

    <!-- Pagination Container -->
    <div
      v-if="allBrands.length > 0"
      ref="paginationContainer"
      class="brands__wrapper__pagination"
    />
  </div>

  <!-- Loading State -->
  <div
    v-if="loading"
    class="loading-state"
  >
    <div class="loading-spinner" />
    <p>Loading brands...</p>
  </div>

  <!-- Error State -->
  <div
    v-if="error"
    class="error-state"
  >
    <p>{{ error }}</p>
  </div>

  <!-- Brands Slider (Always Splide Grid) -->
  <div
    v-if="!loading && !error && displayBrands.length > 0"
    ref="splideContainer"
    :key="`splide-${brandSearch}-${displayBrands.length}`"
    class="splide brands__slider"
  >
    <div class="splide__track">
      <ul class="splide__list">
        <li
          v-for="brand in displayBrands"
          :key="brand.brand_id"
          class="splide__slide brands__slider__slide"
        >
          <a
            :href="`/searchbrand?querybrand=${brand.brand_id}&name=${
              encodeURIComponent(brand.name)
            }`"
            :title="brand.name"
            class="brands__slider__slide__item"
          >
            <img
              :src="brand.image_path"
              :alt="brand.name"
              loading="lazy"
            >
          </a>
        </li>
      </ul>
    </div>
  </div>

  <!-- No Search Results -->
  <div
    v-if="!loading && !error && brandSearch && displayBrands.length === 0"
    class="no-results"
  >
    <p>No brands found matching "{{ brandSearch }}"</p>
  </div>

  <!-- No Brands Available -->
  <div
    v-if="!loading && !error && allBrands.length === 0"
    class="no-brands"
  >
    <p>No brands available</p>
  </div>
</template>

<script>
import Splide from '@splidejs/splide';
import { Grid } from '@splidejs/splide-extension-grid';

export default {
  name: 'BrandsModule',
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      brandSearch: '',
      allBrands: [],
      loading: false,
      error: null,
      splideInstance: null,
      initTimeout: null,
      isInitializing: false,
    };
  },

  computed: {
    displayBrands() {
      if (!this.brandSearch) return this.allBrands;
      // eslint-disable-next-line max-len
      return this.allBrands.filter((brand) => brand.name.toLowerCase().includes(this.brandSearch.toLowerCase()));
    },
  },

  watch: {
    displayBrands() {
      if (this.isInitializing) return;
      this.scheduleInit();
    },
  },

  mounted() {
    if (this.data.brands_id) {
      this.fetchBrandsData();
    }
  },

  beforeUnmount() {
    if (this.initTimeout) clearTimeout(this.initTimeout);
    if (this.splideInstance) this.splideInstance.destroy();
  },

  methods: {
    fetchBrandsData() {
      if (!this.data.brands_id) return;

      this.loading = true;
      this.error = null;

      $.ajax({
        type: 'post',
        url: '/ajax/merchandising',
        headers: {
          'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
        },
        data: {
          groupBrandMerchandising_ids: [this.data.brands_id],
        },
        timeout: 30000,
        success: (response) => {
          this.loading = false;

          if (!response.data?.brandmerchandising) {
            this.allBrands = [];
            this.$forceUpdate();
            return;
          }

          this.allBrands = response.data.brandmerchandising[this.data.brands_id] || [];
          this.$forceUpdate();

          this.$nextTick(() => {
            setTimeout(() => this.scheduleInit(), 100);
          });
        },
        error: (xhr, status, error) => {
          this.loading = false;
          this.error = `Error loading brands: ${status} - ${error}`;
          this.allBrands = [];
          this.$forceUpdate();
        },
      });
    },

    initializeSplide() {
      if (this.splideInstance) {
        try { this.splideInstance.destroy(); } catch (e) {
          console.error(e);
        }
        this.splideInstance = null;
      }

      if (this.displayBrands.length === 0 || !this.$refs.splideContainer) return;

      this.$nextTick(() => {
        if (!this.$refs.splideContainer.querySelectorAll('.splide__slide').length) return;

        try {
          const slideCount = this.displayBrands.length;
          const config = {
            type: 'slide',
            perPage: 1,
            perMove: 1,
            gap: 20,
            arrows: false,
            pagination: slideCount > 10,
            autoplay: false,
            drag: slideCount > 10,
            rewind: false,
            height: '215px',
            grid: {
              rows: 2,
              cols: 5,
              gap: { row: '15px', col: '15px' },
            },
            breakpoints: {
              1400: {
                pagination: slideCount > 8,
                drag: slideCount > 8,
                grid: { rows: 2, cols: 4 },
              },
              1280: {
                pagination: slideCount > 8,
                drag: slideCount > 8,
                grid: { rows: 2, cols: 4 },
              },
              1100: {
                pagination: slideCount > 6,
                drag: slideCount > 6,
                grid: { rows: 2, cols: 3 },
              },
              1000: {
                pagination: slideCount > 4,
                drag: slideCount > 4,
                grid: { rows: 2, cols: 2 },
              },
              900: {
                pagination: slideCount > 4,
                drag: slideCount > 4,
                grid: { rows: 2, cols: 2 },
              },
              768: {
                pagination: slideCount > 6,
                drag: slideCount > 6,
                grid: { rows: 2, cols: 3 },
              },
              600: {
                pagination: slideCount > 4,
                drag: slideCount > 4,
                grid: { rows: 2, cols: 2 },
              },
              400: {
                pagination: slideCount > 2,
                drag: slideCount > 2,
                grid: { rows: 2, cols: 1 },
              },
            },
          };

          this.splideInstance = new Splide(this.$refs.splideContainer, config);
          this.splideInstance.mount({ Grid });
          this.movePagination();
          this.splideInstance.on('resize', () => setTimeout(() => this.movePagination(), 50));
        } catch (error) {
          console.error(error);
        }
      });
    },

    scheduleInit() {
      if (this.isInitializing) return;

      this.isInitializing = true;
      if (this.initTimeout) clearTimeout(this.initTimeout);

      this.initTimeout = setTimeout(() => {
        this.initializeSplide();
        this.isInitializing = false;
      }, 50);
    },

    movePagination() {
      requestAnimationFrame(() => {
        const pagination = this.$refs.splideContainer?.querySelector('.splide__pagination');
        if (pagination && this.$refs.paginationContainer) {
          this.$refs.paginationContainer.appendChild(pagination);
        }
      });
    },
  },
};
</script>
